import React, {useState} from 'react'
import {useNavigate} from 'react-router-dom';
import { formatBetName } from '../../utils/systems/systemUtils';
const SystemPrev = (props) => {
    const system = props.system;
    const navigate = useNavigate();
    const [expandedCriteria, setExpandedCriteria] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    
    // Calculate win percentage for display
    const winPercentage = system.roi !== "NaN" ? (system.w/(system.w+system.l)*100).toFixed(1) : 0;

    // Convert American odds to decimal odds
    const americanToDecimal = (americanOdds) => {
        if (americanOdds > 0) {
            return (americanOdds / 100) + 1;
        } else {
            return (100 / Math.abs(americanOdds)) + 1;
        }
    };

    // Convert decimal odds to American odds
    const decimalToAmerican = (decimalOdds) => {
        if (decimalOdds >= 2) {
            return ((decimalOdds - 1) * 100).toFixed(0);
        } else {
            return (-100 / (decimalOdds - 1)).toFixed(0);
        }
    };

    // Calculate average odds if available
    const avgOdds = system.matches ? 
        Number(decimalToAmerican(
            Object.values(system.matches)
                .flat()
                .reduce((sum, match) => sum + americanToDecimal(parseFloat(match.bet.odds)), 0) / 
                Object.values(system.matches).flat().length
        ))
        : null;

    const handleCriteriaClick = (e) => {
        e.stopPropagation(); // Prevent click from bubbling to parent
        setExpandedCriteria(!expandedCriteria);
    };

    return (
        <div 
            className={`relative overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white/50 dark:bg-gray-800/50 backdrop-blur-xl backdrop-saturate-150 transition-all duration-300 ${
                isHovered ? 'shadow-lg scale-[1.02]' : 'shadow-sm'
            }`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => navigate(`/system/${system._id}`, {state: { system, sysType: props.sysType }})}
        >
            <div className="p-6 space-y-6">
                {/* Header Section */}
                <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                        {system.name}
                    </h3>
                </div>

                {/* Body */}
                <div className="space-y-6">
                    {/* Criteria Section */}
                    <div className="min-h-[76px]">
                        <span className="text-sm text-gray-500 dark:text-gray-400 block mb-3">Criteria</span>
                        <div className="flex flex-wrap gap-2 min-h-[28px] px-1 justify-between">
                            <div className="flex flex-wrap gap-2">
                                {(() => {
                                    if (!expandedCriteria) {
                                        return system.criteria.length > 0 && (
                                            <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                                                {system.criteria[0].label}
                                            </span>
                                        );
                                    }
                                    return system.criteria.map((criterion, index) => (
                                        <span 
                                            key={index}
                                            className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700"
                                        >
                                            {criterion.label}
                                        </span>
                                    ));
                                })()}
                            </div>
                            <div className="flex-shrink-0">
                                {system.criteria.length > 1 ? (
                                    <button
                                        onClick={handleCriteriaClick}
                                        className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-brand-red-50 dark:bg-brand-red-900/20 text-brand-red-600 dark:text-brand-red-200 border border-brand-red-200 dark:border-gray-700 hover:bg-brand-red-100 dark:hover:bg-brand-red-900/30 transition-colors"
                                    >
                                        {expandedCriteria ? 'Show less' : `+${system.criteria.length - 1}`}
                                    </button>
                                ) : (
                                    <div className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium border border-transparent w-[41px]" />
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Bet Type */}
                    <div className="flex items-center justify-between px-1 h-[24px]">
                        <span className="text-sm text-gray-500 dark:text-gray-400">Bet Type</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatBetName(system.bet)}
                        </span>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-px bg-gray-100 dark:bg-gray-700/5 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                        {/* Record */}
                        <div className="bg-white dark:bg-gray-800 px-4 py-6 text-center border-r border-b border-gray-200 dark:border-gray-700">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Record</p>
                            <p className="flex items-baseline justify-center gap-x-2">
                                <span className="text-3xl font-semibold tracking-tight text-gray-900 dark:text-white">
                                    {system.p > 0 ? (system.w +"-"+system.l+"-"+system.p) : (system.w+"-"+system.l)}
                                </span>
                            </p>
                        </div>

                        {/* Avg Odds */}
                        <div className="bg-white dark:bg-gray-800 px-4 py-6 text-center border-b border-gray-200 dark:border-gray-700">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Avg Odds</p>
                            <p className="flex items-baseline justify-center gap-x-2">
                                <span className="text-3xl font-semibold tracking-tight text-gray-900 dark:text-white">
                                    {avgOdds ? (avgOdds > 0 ? '+' : '') + avgOdds : '-'}
                                </span>
                            </p>
                        </div>

                        {/* ROI */}
                        <div className="bg-white dark:bg-gray-800 px-4 py-6 text-center border-r border-gray-200 dark:border-gray-700">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">ROI</p>
                            <p className="flex items-baseline justify-center gap-x-2">
                                <span className={`text-3xl font-semibold tracking-tight ${
                                    system.roi > 0 
                                        ? "text-green-600 dark:text-green-400" 
                                        : "text-red-600 dark:text-red-400"
                                }`}>
                                    {isNaN(system.roi) ? '0' : system.roi}
                                </span>
                                <span className="text-sm text-gray-500 dark:text-gray-400">%</span>
                            </p>
                        </div>

                        {/* Win Rate */}
                        <div className="bg-white dark:bg-gray-800 px-4 py-6 text-center">
                            <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Win Rate</p>
                            <p className="flex items-baseline justify-center gap-x-2">
                                <span className={`text-3xl font-semibold tracking-tight ${
                                    winPercentage > 55 
                                        ? "text-green-600 dark:text-green-400" 
                                        : "text-red-600 dark:text-red-400"
                                }`}>
                                    {winPercentage}
                                </span>
                                <span className="text-sm text-gray-500 dark:text-gray-400">%</span>
                            </p>
                        </div>
                    </div>

                    {/* Last 5 Results */}
                    <div className="flex flex-col items-center h-[52px]">
                        <span className="text-sm text-gray-500 dark:text-gray-400 mb-2">Last 5</span>
                        <div className="flex justify-center gap-1">
                            {system.matches && Object.values(system.matches)
                                .flat()
                                .slice(-5)
                                .reverse()
                                .map((match, idx) => (
                                    <span 
                                        key={idx}
                                        className={`w-2 h-2 rounded-full ${
                                            match.bet.result === 'W' 
                                                ? 'bg-green-500 dark:bg-green-400' 
                                                : match.bet.result === 'L' 
                                                    ? 'bg-red-500 dark:bg-red-400' 
                                                    : 'bg-gray-400 dark:bg-gray-500'
                                        }`}
                                    />
                                ))
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SystemPrev